import React, { useEffect, useMemo, useState } from 'react';
import { withRouter } from "react-router-dom";
import DataTable from 'react-data-table-component';
import moment from 'moment';
import Api from '../Services/Api';
import Alert from '../Services/Alert';
import { getDateFormat } from '../Services/Custom';

const CURRENCY = process.env.REACT_APP_CURRENCY_SYMBOL || '₹';
const money = (v) => `${CURRENCY}${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Money Collected Per Payment Gateway (feature E).
 *
 * Successful gateway deposits only (tnx_type 5, pay_status 1), grouped by the gateway_code
 * stamped on each transaction. Follows the TransactionList pattern: server-side paging, the
 * filter panel re-fetches by flipping isReset, and the summary is returned on page 1 only.
 */
export const MoneyPerGatewayReport = (props) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);

    const [searchFromDate, setSearchFromDate] = useState('');
    const [searchToDate, setSearchToDate] = useState('');
    const [searchGateway, setSearchGateway] = useState('');
    const [search, setSearch] = useState('');
    const [isReset, setIsReset] = useState(false);

    const [gatewayList, setGatewayList] = useState([]);
    const dfltSummary = { gateways: [], grand_count: 0, grand_amount: 0, grand_charge: 0, grand_net: 0 };
    const [summary, setSummary] = useState(dfltSummary);

    const columns = useMemo(() => [
        { name: 'Date', selector: row => moment(row.createdAt).format('DD-MM-YYYY hh:mm A'), width: '170px' },
        { name: 'Gateway', selector: row => row.gateway_code || '-', width: '130px' },
        { name: 'Reference', selector: row => row.tnx || '-' },
        { name: 'UTR / Order', selector: row => row.order_id || '-' },
        { name: 'Mobile', selector: row => row.mobile_no || '-', width: '130px' },
        { name: 'Amount', selector: row => money(row.amount), right: true, width: '120px' },
        { name: 'Charges', selector: row => money(row.charge_amount), right: true, width: '110px' },
        { name: 'Net', selector: row => money(row.net_amount), right: true, width: '120px' },
    ], []);

    function fetchReport(page) {
        setLoading(true);
        const sendd = { page, size: perPage, searchFromDate, searchToDate, searchGateway, search };
        Api.post('report/money-per-gateway', sendd).then(resp => {
            const r = resp.data;
            if (r.status == 1) {
                setData(r.data.docs);
                setTotalRows(r.total);
                if (page === 1) setSummary(r.summaryData || dfltSummary);
            } else {
                Alert.error(r.message);
            }
            setLoading(false);
        }).catch(err => { setLoading(false); Alert.error(err.message); });
    }

    function handlePageChange(page) { fetchReport(page); }
    function handlePerRowsChange(newPerPage, page) {
        setLoading(true);
        const sendd = { page, size: newPerPage, searchFromDate, searchToDate, searchGateway, search };
        Api.post('report/money-per-gateway', sendd).then(resp => {
            const r = resp.data;
            if (r.status == 1) { setData(r.data.docs); setTotalRows(r.total); }
            else Alert.error(r.message);
            setPerPage(newPerPage);
            setLoading(false);
        }).catch(err => { setLoading(false); Alert.error(err.message); });
    }

    const handleFilter = () => setIsReset(!isReset);
    const handleResetFilter = () => {
        setSearchFromDate(''); setSearchToDate(''); setSearchGateway(''); setSearch('');
        setIsReset(!isReset);
    };

    useEffect(() => {
        Api.get('report/gateway-codes').then(resp => {
            if (resp.data.status == 1) setGatewayList(resp.data.data);
        }).catch(() => { /* filter list is optional */ });
    }, []);

    useEffect(() => { fetchReport(1); }, [isReset]);

    return (
        <section className="main-content marketadd_main_sec">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card">
                                <div className="card-body">
                                    <h4 className="card-title mb-4">Money Collected Per Payment Gateway</h4>

                                    <div className="border border-primary p-3 mb-4">
                                        <div className="row">
                                            <div className="col-lg-2">
                                                <label className="form-label">From</label>
                                                <input type="date" className="form-control" max={getDateFormat()}
                                                    value={searchFromDate} onChange={e => setSearchFromDate(e.target.value)} />
                                            </div>
                                            <div className="col-lg-2">
                                                <label className="form-label">To</label>
                                                <input type="date" className="form-control" max={getDateFormat()} min={searchFromDate}
                                                    value={searchToDate} onChange={e => setSearchToDate(e.target.value)} />
                                            </div>
                                            <div className="col-lg-3">
                                                <label className="form-label">Gateway</label>
                                                <select className="form-control" value={searchGateway}
                                                    onChange={e => setSearchGateway(e.target.value)}>
                                                    <option value="">All gateways</option>
                                                    {gatewayList.map(g => <option key={g.code} value={g.code}>{g.name}</option>)}
                                                </select>
                                            </div>
                                            <div className="col-lg-3">
                                                <label className="form-label">Search (reference / UTR)</label>
                                                <input type="text" className="form-control" placeholder="e.g. AAD..."
                                                    value={search} onChange={e => setSearch(e.target.value)} />
                                            </div>
                                            <div className="col-lg-2 d-flex align-items-end">
                                                <button className="btn btn-primary me-2" onClick={handleFilter}>Filter</button>
                                                <button className="btn btn-light" onClick={handleResetFilter}>Reset</button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="table-responsive mb-4">
                                        <table className="table align-middle table-nowrap mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Gateway</th>
                                                    <th className="text-end">Transactions</th>
                                                    <th className="text-end">Collected</th>
                                                    <th className="text-end">Charges</th>
                                                    <th className="text-end">Net</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {summary.gateways && summary.gateways.length > 0 ? summary.gateways.map((g, k) => (
                                                    <tr key={k}>
                                                        <td><span className="badge bg-success">{g.gateway_code}</span></td>
                                                        <td className="text-end">{g.count}</td>
                                                        <td className="text-end">{money(g.total_amount)}</td>
                                                        <td className="text-end">{money(g.total_charge)}</td>
                                                        <td className="text-end">{money(g.net_amount)}</td>
                                                    </tr>
                                                )) : (
                                                    <tr><td colSpan="5" className="text-center">No collections for this filter</td></tr>
                                                )}
                                            </tbody>
                                            {summary.gateways && summary.gateways.length > 0 && (
                                                <tfoot className="table-light">
                                                    <tr>
                                                        <th>Total</th>
                                                        <th className="text-end">{summary.grand_count}</th>
                                                        <th className="text-end">{money(summary.grand_amount)}</th>
                                                        <th className="text-end">{money(summary.grand_charge)}</th>
                                                        <th className="text-end">{money(summary.grand_net)}</th>
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </table>
                                        <small className="text-muted">
                                            Successful deposits only. Charges are shown for gateways that report a fee in
                                            their callback; others show zero.
                                        </small>
                                    </div>

                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        progressPending={loading}
                                        pagination
                                        paginationServer
                                        paginationTotalRows={totalRows}
                                        onChangeRowsPerPage={handlePerRowsChange}
                                        onChangePage={handlePageChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default withRouter(MoneyPerGatewayReport);
